import React, { useState, useEffect } from 'react';
import {
  ArrowDownTrayIcon,
  FunnelIcon,
  CreditCardIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  BanknotesIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { getSupabaseClient } from '../lib/supabase';
import { PaymentDetailsModal } from '../components/PaymentDetailsModal';

interface Payment {
  id: string;
  line_item_id: string;
  client_name: string;
  package_name: string;
  payment_date: string;
  payment_amount: number;
  status: string;
  payment_type: 'deposit' | 'installment';
  installment_number?: number | null;
  total_installments?: number | null;
  order_summary: OrderSummary;
}

interface PaymentSummary {
  total_earnings: number;
  pending_earnings: number;
  upcoming_payments: number;
}

interface OrderInstallment {
  id: string;
  installmentNumber: number | null;
  amountCents: number;
  currency: string;
  dueAt: string | null;
  paidAt: string | null;
  status: string;
  stripeInvoiceId?: string | null;
}

interface OrderTransfer {
  id: string;
  amountCents: number;
  status: string;
  createdAt: string;
  stripeTransferId: string | null;
  transferType?: string | null;
}

interface OrderSummary {
  orderId: string;
  clientName: string;
  packageName: string;
  totalCents: number;
  currency: string;
  status: string;
  createdAt: string;
  scheduleId: string | null;
  subscriptionId: string | null;
  nextInvoiceAt: string | null;
  installmentsTotal: number | null;
  installmentsPaid: number | null;
  depositCents: number;
  durationMonths: number | null;
  installments: OrderInstallment[];
  transfers: OrderTransfer[];
}

interface BalanceData {
  available_to_withdraw_cents: number;
  currency: string;
  payout_schedule: string | null;
}

const paidOrderStatuses = new Set([
  'paid',
  'paid_pending_delivery',
  'succeeded',
  'delivered',
  'completed',
]);

const normalizeInstallmentStatus = (status?: string | null) => {
  if (!status) return 'scheduled';
  const normalized = status.toLowerCase();
  if (['paid', 'succeeded', 'completed'].includes(normalized)) return 'paid';
  if (['scheduled', 'pending', 'pending_payment', 'requires_payment_method'].includes(normalized)) return 'scheduled';
  if (['processing', 'in_progress'].includes(normalized)) return 'processing';
  if (['failed', 'canceled', 'cancelled'].includes(normalized)) return 'failed';
  return normalized;
};

const formatDateLabel = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const PaymentsPage: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [summary, setSummary] = useState<PaymentSummary>({ total_earnings: 0, pending_earnings: 0, upcoming_payments: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [balanceData, setBalanceData] = useState<BalanceData | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [withdrawSuccess, setWithdrawSuccess] = useState<string | null>(null);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [stripeConnected, setStripeConnected] = useState<boolean | null>(null);
  const [stripeAlertDismissed, setStripeAlertDismissed] = useState(false);
  const paymentsPerPage = 7;

  useEffect(() => {
    loadPayments();
  }, []);

  useEffect(() => {
    fetchBalance();
  }, []);

  useEffect(() => {
    checkStripeConnection();
  }, []);

  type PaymentRow = Payment & { sortTimestamp: number };

  const loadPayments = async () => {
    try {
      setLoading(true);
      setError(null);
      const supabase = getSupabaseClient();

      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('Not authenticated');
      }

      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select(`
          id,
          seeker_id,
          total_cents,
          status,
          currency,
          created_at,
          installments_total,
          installments_paid,
          next_invoice_at,
          profiles!orders_seeker_id_fkey (
            full_name
          ),
          package_offers!orders_package_offer_id_fkey (
            name,
            duration_months,
            total_cents,
            subtotal_cents
          ),
          order_installments (
            id,
            installment_number,
            amount_cents,
            currency,
            due_at,
            paid_at,
            status,
            stripe_invoice_id
          ),
          order_transfers (
            id,
            amount_cents,
            status,
            created_at,
            stripe_transfer_id,
            transfer_type
          )
        `)
        .eq('guide_id', user.id)
        .order('created_at', { ascending: false });

      if (ordersError) {
        throw ordersError;
      }

      const paymentRows: PaymentRow[] = [];
      let totalPaidCents = 0;
      let pendingInstallmentCents = 0;
      let upcomingInstallments = 0;

      (ordersData || []).forEach((order: any) => {
        const clientName = order.profiles?.full_name || 'Unknown Client';
        const packageName = order.package_offers?.name || 'Unknown Package';
        const rawInstallments: any[] = order.order_installments ?? [];
        const rawTransfers: any[] = order.order_transfers ?? [];
        const normalizedInstallments: OrderInstallment[] = rawInstallments.map((inst, idx) => ({
          id: inst.id,
          installmentNumber: inst.installment_number ?? idx + 1,
          amountCents: inst.amount_cents,
          currency: inst.currency || order.currency || 'usd',
          dueAt: inst.due_at,
          paidAt: inst.paid_at,
          status: normalizeInstallmentStatus(inst.status),
          stripeInvoiceId: inst.stripe_invoice_id,
        }));
        const normalizedTransfers: OrderTransfer[] = rawTransfers.map((transfer) => ({
          id: transfer.id,
          amountCents: transfer.amount_cents,
          status: transfer.status,
          createdAt: transfer.created_at,
          stripeTransferId: transfer.stripe_transfer_id,
          transferType: transfer.transfer_type || 'order',
        }));
        const totalInstallments =
          (typeof order.installments_total === 'number' ? order.installments_total : null) ??
          (normalizedInstallments.length > 0 ? normalizedInstallments.length : null);

        const referenceTotalCents =
          order.package_offers?.total_cents ??
          order.package_offers?.subtotal_cents ??
          order.total_cents;

        const installmentsTotalCents = normalizedInstallments.reduce(
          (sum, inst) => sum + (inst?.amountCents || 0),
          0
        );
        const depositCents = Math.max(referenceTotalCents - installmentsTotalCents, 0);
        const orderSummary: OrderSummary = {
          orderId: order.id,
          clientName,
          packageName,
          totalCents: referenceTotalCents,
          currency: order.currency || 'usd',
          status: order.status,
          createdAt: order.created_at,
          scheduleId: order.stripe_subscription_schedule_id,
          subscriptionId: order.stripe_subscription_id,
          nextInvoiceAt: order.next_invoice_at,
          installmentsTotal: totalInstallments,
          installmentsPaid: order.installments_paid,
          depositCents,
          durationMonths: order.package_offers?.duration_months ?? null,
          installments: normalizedInstallments,
          transfers: normalizedTransfers,
        };
        const depositPaid = paidOrderStatuses.has((order.status || '').toLowerCase());

        if (depositCents > 0) {
          if (!depositPaid) {
            pendingInstallmentCents += depositCents;
          } else {
            totalPaidCents += depositCents;
          }
          paymentRows.push({
            id: order.id,
            line_item_id: `${order.id}-deposit`,
            client_name: clientName,
            package_name: packageName,
            payment_date: formatDateLabel(order.created_at),
            payment_amount: depositCents / 100,
            status: depositPaid ? 'paid' : 'pending_payment',
            payment_type: 'deposit',
            installment_number: null,
            total_installments: totalInstallments,
            order_summary: orderSummary,
            sortTimestamp: new Date(order.created_at).getTime(),
          });
        }

        normalizedInstallments.forEach((inst) => {
          if (inst.status !== 'paid') {
            pendingInstallmentCents += inst.amountCents;
            if (inst.status === 'scheduled') {
              upcomingInstallments += 1;
            }
          } else {
            totalPaidCents += inst.amountCents;
          }

          const dateSource = inst.paidAt || inst.dueAt || order.created_at;
          paymentRows.push({
            id: order.id,
            line_item_id: inst.id,
            client_name: clientName,
            package_name: packageName,
            payment_date: formatDateLabel(dateSource),
            payment_amount: inst.amountCents / 100,
            status: inst.status,
            payment_type: 'installment',
            installment_number: inst.installmentNumber,
            total_installments: totalInstallments,
            order_summary: orderSummary,
            sortTimestamp: dateSource ? new Date(dateSource).getTime() : new Date(order.created_at).getTime(),
          });
        });

      });

      paymentRows.sort((a, b) => b.sortTimestamp - a.sortTimestamp);
      setPayments(paymentRows.map(({ sortTimestamp, ...rest }) => rest));

      setSummary({
        total_earnings: totalPaidCents / 100,
        pending_earnings: pendingInstallmentCents / 100,
        upcoming_payments: upcomingInstallments,
      });
    } catch (err) {
      console.error('Failed to load payments:', err);
      setError(err instanceof Error ? err.message : 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchBalance = async () => {
    try {
      setStripeError(null);
      setWithdrawSuccess(null);
      setBalanceLoading(true);
      const supabase = getSupabaseClient();
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error('Not authenticated');
      }

      const env = typeof window !== 'undefined' ? (window as any).__ENV__ : undefined;
      const baseUrl = env?.SUPABASE_URL;
      if (!baseUrl) {
        throw new Error('Configuration error. Please refresh and try again.');
      }

      const response = await fetch(`${baseUrl}/functions/v1/connect-balance`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch balance');
      }

      const data = await response.json();
      setBalanceData(data);
    } catch (err) {
      console.error('Error fetching balance:', err);
      setStripeError(err instanceof Error ? err.message : 'Failed to load balance');
    } finally {
      setBalanceLoading(false);
    }
  };

  const checkStripeConnection = async () => {
    try {
      const supabase = getSupabaseClient();
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        return;
      }

      const env = typeof window !== 'undefined' ? (window as any).__ENV__ : undefined;
      const baseUrl = env?.SUPABASE_URL;
      if (!baseUrl) {
        return;
      }

      const response = await fetch(`${baseUrl}/functions/v1/connect-status`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        setStripeConnected(false);
        return;
      }

      const data = await response.json();
      setStripeConnected(data.connected && data.status?.fully_setup);
    } catch (err) {
      console.error('Error checking Stripe connection:', err);
      setStripeConnected(false);
    }
  };

  const handleWithdraw = async () => {
    if (!balanceData || balanceData.available_to_withdraw_cents <= 0 || withdrawLoading) {
      return;
    }

    if (!confirm(`Withdraw ${formatCurrency(balanceData.available_to_withdraw_cents / 100)} to your bank account?`)) {
      return;
    }

    try {
      setWithdrawLoading(true);
      setStripeError(null);
      setWithdrawSuccess(null);

      const supabase = getSupabaseClient();
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session) {
        throw new Error('You must be logged in to withdraw funds');
      }

      const env = typeof window !== 'undefined' ? (window as any).__ENV__ : undefined;
      const baseUrl = env?.SUPABASE_URL;

      if (!baseUrl) {
        throw new Error('Configuration error. Please refresh and try again.');
      }

      const response = await fetch(`${baseUrl}/functions/v1/connect-withdraw`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}), // withdraw full balance
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to process withdrawal');
      }

      const withdrawnCents = result.amount_cents ?? balanceData?.available_to_withdraw_cents ?? 0;
      setWithdrawSuccess(`Withdrawal of ${formatCurrency(withdrawnCents / 100)} initiated successfully!`);
      setBalanceData(prev => prev ? { ...prev, available_to_withdraw_cents: 0 } : null);

      setTimeout(() => {
        setWithdrawSuccess(null);
      }, 5000);

      loadPayments();
      setTimeout(() => fetchBalance(), 1500);
    } catch (err) {
      console.error('Error withdrawing funds:', err);
      setStripeError(err instanceof Error ? err.message : 'Failed to withdraw funds');
    } finally {
      setWithdrawLoading(false);
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(payments.length / paymentsPerPage);
  const startIndex = (currentPage - 1) * paymentsPerPage;
  const endIndex = startIndex + paymentsPerPage;
  const currentPayments = payments.slice(startIndex, endIndex);
  const totalPayments = payments.length;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const generateAvatar = (name: string) => {
    const initials = name.split(' ').map(n => n[0]).join('').toUpperCase();
    const colors = [
      'bg-[#2C2731]',
      'bg-[#3A3340]',
      'bg-[#4B4353]',
      'bg-[#352F3B]',
      'bg-[#433B4A]',
      'bg-[#2E2836]'
    ];
    const colorIndex = name.length % colors.length;

    return (
      <div className={`w-8 h-8 rounded-full ${colors[colorIndex]} flex items-center justify-center text-[#FFFCEF] text-xs font-medium border border-[#FFFCEF]`}>
        {initials}
      </div>
    );
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string }> = {
      'paid': { label: 'Paid', className: 'bg-[#2C2731] text-[#FFFCEF] border border-[#2C2731]' },
      'succeeded': { label: 'Paid', className: 'bg-[#2C2731] text-[#FFFCEF] border border-[#2C2731]' },
      'pending_payment': { label: 'Pending', className: 'bg-[#E8DFBD] text-[#2C2731] border border-[#2C2731]' },
      'requires_payment_method': { label: 'Pending', className: 'bg-[#E8DFBD] text-[#2C2731] border border-[#2C2731]' },
      'active': { label: 'Scheduled', className: 'bg-[#FFFCEF] text-[#2C2731] border border-[#2C2731]/60' },
      'scheduled': { label: 'Scheduled', className: 'bg-[#FFFCEF] text-[#2C2731] border border-[#2C2731]/60' },
    'paid_pending_delivery': { label: 'Paid', className: 'bg-[#2C2731] text-[#FFFCEF] border border-[#2C2731]' },
    'delivered': { label: 'Delivered', className: 'bg-[#F3EBCD] text-[#2C2731] border border-[#2C2731]/40' },
    'cancelled': { label: 'Cancelled', className: 'bg-[#2C2731]/10 text-[#2C2731]/80 border border-[#2C2731]/30' },
    'canceled': { label: 'Cancelled', className: 'bg-[#2C2731]/10 text-[#2C2731]/80 border border-[#2C2731]/30' },
    'refunded': { label: 'Refunded', className: 'bg-[#2C2731]/15 text-[#2C2731] border border-[#2C2731]/30' },
    'processing': { label: 'Processing', className: 'bg-[#F3EBCD] text-[#2C2731] border border-[#2C2731]/40' },
    'failed': { label: 'Failed', className: 'bg-[#FCEDEA] text-[#8C2F2F] border border-[#8C2F2F]/30' },
  };

    const config = statusConfig[status] || { label: status, className: 'bg-[#E8DFBD] text-[#2C2731] border border-[#2C2731]/30' };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const handleExport = () => {
    // Create CSV content
    const headers = ['Client Name', 'Package Name', 'Payment Type', 'Payment Date', 'Amount', 'Status'];
    const rows = payments.map(p => {
      const paymentType = p.payment_type === 'deposit'
        ? 'Deposit'
        : `Installment ${p.installment_number ?? '-'}${p.total_installments ? `/${p.total_installments}` : ''}`;
      return [
        p.client_name,
        p.package_name,
        paymentType,
        p.payment_date,
        formatCurrency(p.payment_amount),
        p.status
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payments-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleFilter = () => {
    alert('Filter functionality would be implemented here');
  };

  if (loading) {
    return (
      <div className="p-6 bg-[#FFFCEF] text-[#2C2731] min-h-full">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="text-[#2C2731]/70">Loading payments...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-[#FFFCEF] text-[#2C2731] min-h-full">
        <div className="max-w-7xl mx-auto">
          <div className="bg-[#2C2731]/10 border border-[#2C2731] rounded-lg p-4 text-[#2C2731]">
            <p>{error}</p>
            <button
              onClick={loadPayments}
              className="mt-2 inline-flex items-center px-3 py-1.5 text-sm border border-[#2C2731] rounded-md bg-[#2C2731] text-[#FFFCEF] hover:bg-[#241F2A] transition-colors"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const availableBalanceCents = balanceData?.available_to_withdraw_cents ?? 0;
  const payoutScheduleLabel = balanceData?.payout_schedule ?? 'manual';

  return (
    <div className="p-6 bg-[#FFFCEF] text-[#2C2731] min-h-full">
      <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-medium font-recoleta text-[#2C2731] mb-1">Payments</h1>
        <p className="text-sm text-[#2C2731]/80">Track your earnings and payment history</p>
      </div>

      {/* Stripe Connection Alert */}
      {stripeConnected === false && !stripeAlertDismissed && (
        <div className="mb-6 rounded-lg border border-amber-500 bg-amber-50 p-4">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-amber-800">
                Stripe not connected
              </h3>
              <p className="mt-1 text-sm text-amber-700">
                Connect your Stripe account to receive payments from seekers. You won't be able to send package offers until Stripe is set up.
              </p>
              <div className="mt-3">
                <button
                  onClick={() => {
                    window.location.hash = 'settings-payments';
                  }}
                  className="inline-flex items-center rounded-md bg-amber-600 px-3 py-2 text-sm font-medium text-white hover:bg-amber-700 transition-colors"
                >
                  <CreditCardIcon className="h-4 w-4 mr-1.5" />
                  Set Up Payments
                </button>
              </div>
            </div>
            <button
              onClick={() => setStripeAlertDismissed(true)}
              className="ml-4 flex-shrink-0 rounded p-1 text-amber-500 hover:bg-amber-100 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Total Earnings Card */}
        <div className="bg-[#FFFCEF] border border-[#2C2731] rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-[#E8DFBD] border border-[#2C2731] rounded-lg flex items-center justify-center mr-3 text-[#2C2731]">
                <span className="text-lg font-semibold">$</span>
              </div>
              <div>
                <p className="text-2xl font-semibold">{formatCurrency(summary.total_earnings)}</p>
                <p className="text-sm text-[#2C2731]/80">
                  Total Earnings<br />
                  <span className="text-xs">Paid deposits & installments</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Pending Earnings Card */}
        <div className="bg-[#FFFCEF] border border-[#2C2731] rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-[#E8DFBD] border border-[#2C2731] rounded-lg flex items-center justify-center mr-3 text-[#2C2731]">
                <CreditCardIcon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{formatCurrency(summary.pending_earnings)}</p>
                <p className="text-sm text-[#2C2731]/80">
                  Pending Earnings<br />
                  <span className="text-xs">From scheduled payments</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Upcoming Payments Card */}
        <div className="bg-[#FFFCEF] border border-[#2C2731] rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-[#E8DFBD] border border-[#2C2731] rounded-lg flex items-center justify-center mr-3 text-[#2C2731]">
                <span className="text-lg font-semibold">#</span>
              </div>
              <div>
                <p className="text-2xl font-semibold">{summary.upcoming_payments}</p>
                <p className="text-sm text-[#2C2731]/80">
                  Upcoming Payments<br />
                  <span className="text-xs">Scheduled installments</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Balance + Withdraw */}
      <div className="mb-8">
        <div className="bg-[#FFFCEF] border border-[#2C2731] rounded-lg p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#E8DFBD] border border-[#2C2731] flex items-center justify-center text-[#2C2731]">
                <BanknotesIcon className="w-6 h-6" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#2C2731]/60">Available to withdraw</p>
                <p className="text-2xl font-semibold text-[#2C2731]">
                  {balanceLoading ? 'Checking…' : formatCurrency(availableBalanceCents / 100)}
                </p>
                <p className="text-xs text-[#2C2731]/60">
                  Payout schedule: {payoutScheduleLabel}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={fetchBalance}
                disabled={balanceLoading}
                className="inline-flex items-center px-3 py-1.5 text-sm border border-[#2C2731] rounded-md text-[#2C2731] bg-[#FFFCEF] hover:bg-[#F3EBCD] disabled:opacity-50"
              >
                <ArrowPathIcon className={`w-4 h-4 mr-1 ${balanceLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={handleWithdraw}
                disabled={availableBalanceCents <= 0 || withdrawLoading || balanceLoading}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md border border-[#2C2731] bg-[#2C2731] text-[#FFFCEF] hover:bg-[#1F1924] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {withdrawLoading ? 'Processing...' : 'Withdraw'}
              </button>
            </div>
          </div>
          {stripeError && (
            <p className="mt-3 text-sm text-red-700">{stripeError}</p>
          )}
          {withdrawSuccess && (
            <p className="mt-3 text-sm text-green-700">{withdrawSuccess}</p>
          )}
        </div>
      </div>

      {/* Payment History */}
      <div className="bg-[#FFFCEF] border border-[#2C2731] rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-[#2C2731] flex items-center justify-between bg-[#2C2731] text-[#FFFCEF]">
          <h2 className="text-base font-medium font-recoleta">Payment History</h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleExport}
              disabled={payments.length === 0}
              className="inline-flex items-center px-3 py-1.5 border border-[#FFFCEF] text-sm font-medium rounded-md text-[#2C2731] bg-[#FFFCEF] hover:bg-[#F3EBCD] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
              Export
            </button>
            <button
              onClick={handleFilter}
              className="inline-flex items-center px-3 py-1.5 border border-[#FFFCEF] text-sm font-medium rounded-md text-[#2C2731] bg-[#FFFCEF] hover:bg-[#F3EBCD] transition-colors"
            >
              <FunnelIcon className="w-4 h-4 mr-1" />
              Filter
            </button>
          </div>
        </div>

        {payments.length === 0 ? (
          <div className="px-6 py-12 text-center text-[#2C2731]/70">
            <p>No payments yet</p>
          </div>
        ) : (
          <>
            {/* Table Header - Hidden on mobile */}
            <div className="hidden lg:block px-6 py-3 bg-[#E8DFBD] border-b border-[#2C2731]/30 text-[#2C2731]">
              <div className="grid grid-cols-6 gap-4 text-xs font-medium uppercase tracking-wider">
                <div>Client</div>
                <div>Package</div>
                <div>Type</div>
                <div>Date</div>
                <div>Amount</div>
                <div>Status</div>
              </div>
            </div>

            {/* Table Body - Desktop */}
            <div className="hidden lg:block divide-y divide-[#2C2731]/20">
              {currentPayments.map((payment) => (
                <div
                  key={payment.line_item_id}
                  className="px-6 py-4 hover:bg-[#F3EBCD] cursor-pointer transition-colors"
                  onClick={() => setSelectedPayment(payment)}
                >
                  <div className="grid grid-cols-6 gap-4 items-center">
                    <div className="flex items-center space-x-3">
                      {generateAvatar(payment.client_name)}
                      <span className="text-sm font-medium text-[#2C2731]">{payment.client_name}</span>
                    </div>
                    <div className="text-sm text-[#2C2731]">{payment.package_name}</div>
                    <div className="text-xs text-[#2C2731]/70">
                      {payment.payment_type === 'deposit'
                        ? 'Deposit'
                        : `Installment ${payment.installment_number ?? '-'}${payment.total_installments ? `/${payment.total_installments}` : ''}`}
                    </div>
                    <div className="text-sm text-[#2C2731]/70">{payment.payment_date}</div>
                    <div className="text-sm font-medium text-[#2C2731]">{formatCurrency(payment.payment_amount)}</div>
                    <div>{getStatusBadge(payment.status)}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile Card Layout */}
            <div className="lg:hidden divide-y divide-[#2C2731]/20">
              {currentPayments.map((payment) => (
                <div
                  key={payment.line_item_id}
                  className="p-4 hover:bg-[#F3EBCD] cursor-pointer transition-colors"
                  onClick={() => setSelectedPayment(payment)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      {generateAvatar(payment.client_name)}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-[#2C2731] truncate">
                            {payment.client_name}
                          </span>
                          {getStatusBadge(payment.status)}
                        </div>
                        <p className="text-xs text-[#2C2731]/70 truncate mt-0.5">
                          {payment.package_name}
                        </p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-semibold text-[#2C2731]">
                        {formatCurrency(payment.payment_amount)}
                      </p>
                      <p className="text-xs text-[#2C2731]/60 mt-0.5">
                        {payment.payment_date}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-[#2C2731]/60">
                    {payment.payment_type === 'deposit'
                      ? 'Deposit'
                      : `Installment ${payment.installment_number ?? '-'}${payment.total_installments ? `/${payment.total_installments}` : ''}`}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-4 sm:px-6 py-4 border-t border-[#2C2731]/30 text-[#2C2731]">
                {/* Mobile pagination */}
                <div className="flex flex-col gap-3 sm:hidden">
                  <div className="text-xs text-[#2C2731]/80 text-center">
                    {startIndex + 1}-{Math.min(endIndex, totalPayments)} of {totalPayments}
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="p-2 border border-[#2C2731] rounded-md text-[#2C2731] bg-[#FFFCEF] hover:bg-[#F3EBCD] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeftIcon className="w-4 h-4" />
                    </button>
                    <span className="text-sm font-medium px-3">
                      {currentPage} / {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="p-2 border border-[#2C2731] rounded-md text-[#2C2731] bg-[#FFFCEF] hover:bg-[#F3EBCD] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRightIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Desktop pagination */}
                <div className="hidden sm:flex items-center justify-between">
                  <div className="text-sm text-[#2C2731]/80">
                    Showing {startIndex + 1} to {Math.min(endIndex, totalPayments)} of {totalPayments} payments
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm border border-[#2C2731] rounded-md text-[#2C2731] bg-[#FFFCEF] hover:bg-[#F3EBCD] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>

                    {[...Array(totalPages)].map((_, index) => {
                      const pageNumber = index + 1;
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => setCurrentPage(pageNumber)}
                          className={`px-3 py-1 text-sm rounded-md ${
                            currentPage === pageNumber
                              ? 'bg-[#2C2731] text-[#FFFCEF]'
                              : 'border border-[#2C2731] text-[#2C2731] bg-[#FFFCEF] hover:bg-[#F3EBCD]'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    })}

                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm border border-[#2C2731] rounded-md text-[#2C2731] bg-[#FFFCEF] hover:bg-[#F3EBCD] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Payment Details Modal */}
      {selectedPayment && (
        <PaymentDetailsModal
          payment={selectedPayment}
          onClose={() => setSelectedPayment(null)}
        />
      )}
      </div>
    </div>
  );
};

export default PaymentsPage;
